/** Cancel a subscription with a verified pay-api mode (TMMPDM-0008). */
import { useState } from 'react';
import {
  Alert,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  FormControl,
  FormControlLabel,
  FormLabel,
  Radio,
  RadioGroup,
  Stack,
  Typography,
} from '@mui/material';
import { useCopy } from '@/hooks/useCopy';
import { SubmitButton } from '@/components/page';
import { adminApi } from '@/api/tmm/admin';
import type { CancelMode, CancelResponse } from '@/api/tmm/types';
import { errorCopy } from '@/api/errors';
import { formatDate } from '@/format';

interface CancelSubscriptionDialogProps {
  open: boolean;
  slug: string;
  subscriptionId: number;
  /** Who the subscription is for — the object the title names. */
  subscriber: string;
  locale: string;
  onClose: () => void;
  onDone: () => void;
}

const MODES: CancelMode[] = ['at_period_end', 'immediate'];

export function CancelSubscriptionDialog({
  open,
  slug,
  subscriptionId,
  subscriber,
  locale,
  onClose,
  onDone,
}: CancelSubscriptionDialogProps) {
  // Held here, so Esc and a click outside wait for a request that is running (as ConfirmDialog does).
  const [submitting, setSubmitting] = useState(false);
  return (
    <Dialog
      open={open}
      onClose={() => {
        if (!submitting) onClose();
      }}
      fullWidth
      maxWidth="sm"
    >
      {open ? (
        <CancelSubscriptionForm
          slug={slug}
          subscriptionId={subscriptionId}
          subscriber={subscriber}
          locale={locale}
          submitting={submitting}
          setSubmitting={setSubmitting}
          onClose={onClose}
          onDone={onDone}
        />
      ) : null}
    </Dialog>
  );
}

function CancelSubscriptionForm({
  slug,
  subscriptionId,
  subscriber,
  locale,
  submitting,
  setSubmitting,
  onClose,
  onDone,
}: Omit<CancelSubscriptionDialogProps, 'open'> & {
  submitting: boolean;
  setSubmitting: (submitting: boolean) => void;
}) {
  const { t } = useCopy();
  const [mode, setMode] = useState<CancelMode>('at_period_end');
  const [error, setError] = useState<string | null>(null);
  const [result, setResult] = useState<CancelResponse | null>(null);

  const confirm = async () => {
    setSubmitting(true);
    setError(null);
    try {
      const response = await adminApi.cancelSubscription(slug, subscriptionId, { mode });
      setResult(response);
      onDone();
    } catch (err: unknown) {
      setError(
        errorCopy(err, t, t('subscriptions.cancel.error', "Couldn't cancel the subscription.")),
      );
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <>
      <DialogTitle>
        {result
          ? t('subscriptions.cancel.title', 'Cancel subscription')
          : t('subscriptions.cancel.titleNamed', 'Cancel the subscription for {{subscriber}}?', {
              subscriber,
            })}
      </DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ mt: 1 }}>
          {result ? null : (
            <DialogContentText>
              {mode === 'immediate'
                ? t(
                    'subscriptions.cancel.consequence.immediate',
                    'The subscriber loses access now, and a final statement and charge may follow.',
                  )
                : t(
                    'subscriptions.cancel.consequence.at_period_end',
                    "It won't renew. The subscriber keeps access until the end of the current period.",
                  )}
            </DialogContentText>
          )}
          {error ? <Alert severity="error">{error}</Alert> : null}
          {result ? (
            <>
              <Typography>
                {t('subscriptions.cancel.resultStatus', 'Status')}:{' '}
                {t(`subscription.status.${result.status}`, result.status)}
              </Typography>
              <Typography>
                {t('subscriptions.cancel.endsAt', 'Ends at')}: {formatDate(result.endsAt, locale)}
              </Typography>
            </>
          ) : (
            <FormControl>
              <FormLabel id="cancel-mode-label">{t('subscriptions.cancel.mode', 'Mode')}</FormLabel>
              <RadioGroup
                aria-labelledby="cancel-mode-label"
                value={mode}
                onChange={(event) => setMode(event.target.value as CancelMode)}
              >
                {MODES.map((value) => (
                  <FormControlLabel
                    key={value}
                    value={value}
                    control={<Radio />}
                    label={t(`subscriptions.cancel.${value}`, value)}
                  />
                ))}
              </RadioGroup>
            </FormControl>
          )}
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={submitting}>
          {result
            ? t('common.close', 'Close')
            : t('subscriptions.cancel.keep', 'Keep subscription')}
        </Button>
        {result ? null : (
          <SubmitButton color="error" onClick={() => void confirm()} busy={submitting}>
            {t('subscriptions.cancel.confirm', 'Cancel subscription')}
          </SubmitButton>
        )}
      </DialogActions>
    </>
  );
}
